/**
 * offlineQueue — Fila de mutações para retry quando a conexão voltar.
 *
 * Use case principal: cliente clica "Finalizar pedido" no checkout, mas perde
 * sinal exatamente no momento. Em vez de erro feio, salvamos no localStorage
 * e reenviamos quando ele voltar online.
 *
 * Storage: localStorage (limite ~5MB, ok pra ~1000 pedidos pendentes).
 * Persistência: sobrevive a reload/fechamento do app.
 * Trigger de retry: window.online event + chamada manual drain() ao subir o app.
 *
 * API:
 *   enqueue({url, method, body, headers, label, onSuccess, onFailure})
 *   drain()  -> processa todos os pendentes
 *   subscribe(listener) -> notifica mudanças na fila (pra UI mostrar contador)
 *   getQueue() -> lista atual
 */
import { toast } from "sonner";

const STORAGE_KEY = "gofood_offline_queue_v1";
const MAX_RETRIES = 6;            // tenta até 6 vezes
const RETRY_BACKOFF_MS = 3000;    // espera 3s entre tentativas
const listeners = new Set();

function _read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function _write(arr) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch (e) {
    console.warn("offlineQueue: localStorage write failed", e);
  }
  listeners.forEach((fn) => {
    try { fn(arr); } catch { /* ignore listener errors */ }
  });
}

export function getQueue() {
  return _read();
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(_read());
  return () => listeners.delete(fn);
}

export function enqueue({ url, method = "POST", body, headers = {}, label, onSuccessUrl }) {
  const item = {
    id: Math.random().toString(36).slice(2) + Date.now().toString(36),
    url,
    method,
    body: body ?? null,
    headers,
    label: label || "Operação",
    onSuccessUrl: onSuccessUrl || null,
    attempts: 0,
    createdAt: Date.now(),
  };
  const q = _read();
  q.push(item);
  _write(q);
  return item;
}

async function _tryOne(item) {
  try {
    const r = await fetch(item.url, {
      method: item.method,
      headers: { "Content-Type": "application/json", ...item.headers },
      body: item.body ? JSON.stringify(item.body) : undefined,
    });
    if (r.status >= 500) throw new Error(`server ${r.status}`);
    // 4xx é "permanente" — não reentregamos pra evitar duplicação acidental
    return { ok: r.ok, status: r.status, response: await r.json().catch(() => ({})) };
  } catch (e) {
    return { ok: false, networkError: true, error: String(e) };
  }
}

let _draining = false;
export async function drain({ silent = false } = {}) {
  if (_draining) return;
  if (!navigator.onLine) return;
  _draining = true;
  try {
    let q = _read();
    if (!q.length) return;
    if (!silent) toast.info(`Sincronizando ${q.length} ${q.length === 1 ? "ação" : "ações"} pendentes...`);

    const remaining = [];
    for (const item of q) {
      item.attempts = (item.attempts || 0) + 1;
      const result = await _tryOne(item);
      if (result.ok) {
        if (!silent) toast.success(`✅ ${item.label} enviada`);
        // Se houver URL de redirect (ex.: tracking do pedido), navega
        if (item.onSuccessUrl && result.response?.id) {
          const targetUrl = item.onSuccessUrl.replace(":id", result.response.id);
          // Adia navegação pra não atrapalhar UX se múltiplos itens
          setTimeout(() => { try { window.location.href = targetUrl; } catch { /* ignore */ } }, 300);
        }
      } else if (!result.networkError && result.status && result.status < 500) {
        // Erro permanente (4xx) — descarta com aviso
        if (!silent) toast.error(`❌ ${item.label}: ${result.response?.detail || "Rejeitada"}`);
      } else if (item.attempts < MAX_RETRIES) {
        // Falha de rede ou 5xx — re-enfileira
        remaining.push(item);
      } else {
        // Excedeu retries
        if (!silent) toast.error(`❌ ${item.label}: falhou após ${MAX_RETRIES} tentativas`);
      }
      // Pequeno espaço entre requests pra não martelar o servidor
      await new Promise((res) => setTimeout(res, 250));
    }
    _write(remaining);

    // Se ainda sobrou alguma com erro de rede, agenda nova tentativa com backoff
    if (remaining.length) {
      setTimeout(() => drain({ silent: true }), RETRY_BACKOFF_MS);
    }
  } finally {
    _draining = false;
  }
}

// Inicialização automática — drena quando vier online + ao subir o app.
let _bootstrapped = false;
export function bootstrapOfflineQueue() {
  if (_bootstrapped) return;
  _bootstrapped = true;
  window.addEventListener("online", () => drain({ silent: false }));
  // Tenta drenar imediatamente caso o app tenha aberto online já com fila
  setTimeout(() => drain({ silent: true }), 2500);
}

/**
 * postWithOfflineFallback — wrapper de fetch que enfileira se rede cair.
 * Use em chamadas críticas (criar pedido, salvar endereço, avaliar, etc.).
 *
 * Retorna { online: bool, response?, queuedItem? }.
 */
export async function postWithOfflineFallback({ url, body, headers, label, onSuccessUrl }) {
  if (!navigator.onLine) {
    const item = enqueue({ url, method: "POST", body, headers, label, onSuccessUrl });
    toast.warning(`📡 Sem internet — ${label} salvo. Enviaremos quando voltar online.`);
    return { online: false, queuedItem: item };
  }
  try {
    const r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(headers || {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok && r.status >= 500) {
      // 5xx — provavelmente backend caiu; enfileira
      const item = enqueue({ url, method: "POST", body, headers, label, onSuccessUrl });
      toast.warning(`Servidor instável — ${label} salvo. Tentaremos novamente.`);
      return { online: false, queuedItem: item };
    }
    const data = await r.json().catch(() => ({}));
    return { online: true, response: data, status: r.status, ok: r.ok };
  } catch (e) {
    // Falha de rede no meio do request
    const item = enqueue({ url, method: "POST", body, headers, label, onSuccessUrl });
    toast.warning(`📡 Conexão perdida — ${label} salvo. Enviaremos quando voltar online.`);
    return { online: false, queuedItem: item };
  }
}
